import React, { useState } from 'react';
import {
  ClockIcon,
  InfoIcon,
  MailIcon,
  MoonIcon,
  PencilIcon,
  PhoneForwardedIcon,
  PlusIcon,
  RouteIcon,
  Trash2Icon,
  XCircleIcon,
} from 'lucide-react';
import { z } from 'zod';

import { FormError } from '@/components/atoms/form-label';
import { PhoneInput } from '@/components/atoms/phone-input';
import { Tabs } from '@/components/atoms/tabs';
import { ToggleButton } from '@/components/atoms/toggle-button';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { cn, hasErrors } from '@/lib/utils';

import { useVoiceSettingsCtx } from './provider';

export function CallRoutingSettings() {
  return (
    <div className='flex w-full flex-col gap-4'>
      <EscalationSettings />
      <RoutingSettings />
    </div>
  );
}

export function EscalationCallout() {
  return (
    <div className='flex w-full items-center gap-2 rounded-md border border-blue-500/20 bg-blue-500/5 px-3 py-2 text-blue-500'>
      <InfoIcon className='size-4' />
      <div className='text-xs font-medium'>
        The notification will use the &quot;Default Transfer & Notification Settings&quot; configured in{' '}
        <span className='underline'>Call Routing</span> tab.
      </div>
    </div>
  );
}

function EscalationSettings() {
  const {
    settings: { escalation },
    onChange: onSettingsChange,
    errors,
  } = useVoiceSettingsCtx();

  const onChange = (v: Partial<typeof escalation>) => {
    onSettingsChange({ escalation: { ...escalation, ...v } });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Default Transfer & Notification Settings</CardTitle>
        <CardDescription>
          Configure the default way calls are transferred or emails are sent when the AI action or behavior triggers an
          escalation. These settings apply globally unless overridden by a specific routing rule below.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs
          defaultValue='in_hours'
          rootClassName='gap-4'
          listClassName='w-full'
          options={[
            {
              value: 'in_hours',
              label: (
                <div className='flex items-center gap-2'>
                  <ClockIcon className='size-4' />
                  <span>Office Hours</span>
                </div>
              ),
            },
            {
              value: 'after_hours',
              label: (
                <div className='flex items-center gap-2'>
                  <MoonIcon className='size-4' />
                  <span>After Hours</span>
                </div>
              ),
            },
          ]}
        >
          <TabsContent value='in_hours'>
            <EscalationPolicy
              value={
                escalation.policy_in_hours ?? {
                  type: 'TRANSFER',
                  value: '',
                  execution_message: '',
                }
              }
              setValue={(v) => onChange({ policy_in_hours: v })}
              errors={errors?.escalation?.policy_in_hours}
            />
          </TabsContent>
          <TabsContent value='after_hours'>
            <EscalationPolicy
              value={
                escalation.policy_after_hours ?? {
                  type: 'TRANSFER',
                  value: '',
                  execution_message: '',
                }
              }
              setValue={(v) => onChange({ policy_after_hours: v })}
              errors={errors?.escalation?.policy_after_hours}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

interface EscalationPolicyProps {
  className?: string;
  value: ApiSchemas['EscalationPolicy'];
  setValue: (v: ApiSchemas['EscalationPolicy']) => void;
  errors:
    | z.ZodFormattedError<{
        type: 'TRANSFER' | 'EMAIL';
        value: string;
      }>
    | undefined;
}

function EscalationPolicy({ value, setValue, className, errors }: EscalationPolicyProps) {
  const onChange = (v: Partial<typeof value>) => {
    setValue({
      ...value,
      ...v,
    });
  };

  const [drafts, setDrafts] = useState<{
    TRANSFER: { value: string; execution_message: string };
    EMAIL: { value: string; execution_message: string };
  }>(() => ({
    TRANSFER:
      value?.type === 'TRANSFER'
        ? { value: value.value, execution_message: value.execution_message }
        : { value: '', execution_message: '' },
    EMAIL:
      value?.type === 'EMAIL'
        ? { value: value.value, execution_message: value.execution_message }
        : { value: '', execution_message: '' },
  }));

  const selectType = (type: 'TRANSFER' | 'EMAIL') => {
    const next = drafts[type];
    setValue({ type, value: next.value, execution_message: next.execution_message });
  };

  const updateCurrentDraft = (partial: { value?: string; execution_message?: string }) => {
    const currentType = value?.type;
    if (!currentType) return;
    setDrafts((prev) => ({
      ...prev,
      [currentType]: {
        value: partial.value ?? prev[currentType].value,
        execution_message: partial.execution_message ?? prev[currentType].execution_message,
      },
    }));
  };

  return (
    <div className={cn('flex w-full flex-col gap-4', className)}>
      <div className='space-y-1.5'>
        <Label className='px-1'>Action Type</Label>
        <div className='grid grid-cols-2 gap-2'>
          <ToggleButton
            selected={value?.type === 'TRANSFER'}
            toggle={() => selectType('TRANSFER')}
            className='flex-col items-center justify-center'
          >
            <PhoneForwardedIcon className='size-4' />
            Transfer
          </ToggleButton>
          <ToggleButton
            selected={value?.type === 'EMAIL'}
            toggle={() => selectType('EMAIL')}
            className='flex-col items-center justify-center'
          >
            <MailIcon className='size-4' />
            Email
          </ToggleButton>
        </div>
      </div>
      {!value && (
        <div className='text-muted-foreground px-2 text-sm'>
          No action will be taken. Customer will be told that someone will get back to them soon. Call will appear in
          the dashboard.
        </div>
      )}
      {value?.type === 'TRANSFER' && (
        <div className='space-y-1.5'>
          <Label className='px-1'>Phone number to transfer to</Label>
          <PhoneInput
            value={value.value}
            onChange={(newValue) => {
              updateCurrentDraft({ value: newValue });
              onChange({ value: newValue });
            }}
            placeholder='+1 (555) 123-4567'
            errors={errors?.value?._errors}
          />
        </div>
      )}
      {value?.type === 'EMAIL' && (
        <div className='space-y-1.5'>
          <Label className='px-1'>Email address to notify</Label>
          <Input
            value={value.value}
            onChange={(e) => {
              updateCurrentDraft({ value: e.target.value });
              onChange({ value: e.target.value });
            }}
            placeholder='example@example.com'
            aria-invalid={hasErrors(errors?.value?._errors)}
          />
          <FormError errors={errors?.value?._errors} />
        </div>
      )}
      {value && (
        <div className='space-y-1.5'>
          <Label className='px-1'>Execution Message</Label>
          <Textarea
            value={value.execution_message}
            onChange={(e) => {
              updateCurrentDraft({ execution_message: e.target.value });
              onChange({ execution_message: e.target.value });
            }}
          />
          <p className='text-muted-foreground text-sm'>
            The script the Voice Agent will use while performing the action.
          </p>
        </div>
      )}
    </div>
  );
}

function RoutingSettings() {
  const {
    settings: { routing },
    onChange,
  } = useVoiceSettingsCtx();

  const onRuleChange = (index: number, rule: Partial<RoutingRule>) => {
    onChange({
      routing: {
        ...routing,
        rules: [
          ...routing.rules.slice(0, index),
          { ...routing.rules[index], ...rule },
          ...routing.rules.slice(index + 1),
        ],
      },
    });
  };

  const onRuleDelete = (index: number) => {
    onChange({
      routing: {
        ...routing,
        rules: routing.rules.filter((_, i) => i !== index),
      },
    });
  };

  return (
    <Card>
      <CardHeader className='flex flex-row items-center justify-between gap-6'>
        <div className='space-y-1.5'>
          <CardTitle>Routing Settings</CardTitle>
          <CardDescription>
            Define rules for routing calls to specific people or departments. These can override the default transfer
            settings if a more specific match is found.
          </CardDescription>
        </div>
        <RoutingRuleDialog
          initial={{
            name: '',
            description: '',
            destination_in_hours: null,
            destination_after_hours: null,
          }}
          onSubmit={(rule) => {
            onChange({
              routing: {
                ...routing,
                rules: [rule, ...routing.rules],
              },
            });
          }}
          trigger={
            <Button variant='outline' size='lg'>
              <PlusIcon className='size-4' />
              Add Rule
            </Button>
          }
          title='Add Rule'
        />
      </CardHeader>
      <CardContent className='flex flex-col gap-4'>
        {routing.rules.map((rule, index) => (
          <RoutingRule
            key={index}
            rule={rule}
            onChange={(v) => onRuleChange(index, v)}
            onDelete={() => onRuleDelete(index)}
          />
        ))}
        <div className='space-y-1.5'>
          <Label className='px-1'>Unavailable Script</Label>
          <Textarea
            value={routing.unavailable_script || ''}
            onChange={(e) =>
              onChange({
                routing: { ...routing, unavailable_script: e.target.value },
              })
            }
            placeholder="I'm sorry, but I'm unable to transfer you at the moment..."
          />
          <p className='text-muted-foreground text-sm'>
            If a transfer cannot be completed because nobody picks up, this script will be used.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

type RoutingRule = ApiSchemas['RoutingRule-Input'];

interface RoutingRuleProps {
  rule: RoutingRule;
  onChange: (rule: Partial<RoutingRule>) => void;
  onDelete: () => void;
}

function RoutingRule({ rule, onChange, onDelete }: RoutingRuleProps) {
  return (
    <div className='rounded-md border p-4'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <div className='bg-muted rounded-md p-3'>
            <RouteIcon className='size-4' />
          </div>
          <div className=''>
            <div className='text-sm font-medium'>{rule.name}</div>
            {rule.description && <div className='text-muted-foreground text-xs'>{rule.description}</div>}
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <RoutingRuleDialog
            initial={rule}
            onSubmit={(v) => onChange(v)}
            trigger={
              <Button variant='ghost' size='icon'>
                <PencilIcon className='size-4' />
              </Button>
            }
            title='Edit Rule'
          />
          <Button variant='ghost' size='icon' onClick={onDelete}>
            <Trash2Icon className='size-4' />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface RoutingRuleDialogProps {
  initial: RoutingRule;
  onSubmit: (rule: RoutingRule) => void;
  trigger: React.ReactNode;
  title: string;
}

function RoutingRuleDialog({ initial, onSubmit, trigger, title }: RoutingRuleDialogProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(initial);

  const onChange = (v: Partial<typeof state>) => {
    setState((prev) => ({ ...prev, ...v }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent showClose className='max-h-[90vh] max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Define how calls are routed to the appropriate team or agent during and outside of office hours.
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          <div className='space-y-1.5'>
            <Label>Rule Name</Label>
            <Input
              value={state.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder='e.g. Sales Team, High Priority Support'
            />
          </div>
          <div className='space-y-1.5'>
            <Label>Description</Label>
            <Textarea
              value={state.description}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder='e.g. Sales Team, High Priority Support'
            />
          </div>
          <RoutingRuleDestinationInput
            icon={<ClockIcon className='size-4' />}
            label='Office Hours Action'
            value={state.destination_in_hours}
            onChange={(v) => onChange({ destination_in_hours: v })}
          />
          <RoutingRuleDestinationInput
            icon={<MoonIcon className='size-4' />}
            label='After Hours Action'
            value={state.destination_after_hours}
            onChange={(v) => onChange({ destination_after_hours: v })}
          />
        </div>
        <DialogFooter>
          <Button
            onClick={() => {
              onSubmit(state);
              setOpen(false);
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type RoutingRuleDestination =
  | ApiSchemas['RoutingRule-Input']['destination_in_hours']
  | ApiSchemas['RoutingRule-Input']['destination_after_hours'];

interface RoutingRuleDestinationInputProps {
  icon: React.ReactNode;
  label: string;
  value: RoutingRuleDestination;
  onChange: (v: RoutingRuleDestination) => void;
}

function RoutingRuleDestinationInput({ icon, label, value, onChange }: RoutingRuleDestinationInputProps) {
  return (
    <div className='space-y-4 rounded-md border p-5'>
      <div className='space-y-2.5'>
        <Label>
          {icon}
          {label}
        </Label>
        <div className='grid w-full grid-cols-3 gap-2'>
          <ToggleButton
            selected={!value?.type}
            toggle={() => onChange(null)}
            className='flex-col items-center justify-center'
          >
            <XCircleIcon className='size-4' />
            No Action
          </ToggleButton>
          <ToggleButton
            selected={value?.type === 'TRANSFER'}
            toggle={() => onChange({ type: 'TRANSFER', value: '' })}
            className='flex-col items-center justify-center'
          >
            <PhoneForwardedIcon className='size-4' />
            Transfer
          </ToggleButton>
          <ToggleButton
            selected={value?.type === 'EMAIL'}
            toggle={() => onChange({ type: 'EMAIL', value: '' })}
            className='flex-col items-center justify-center'
          >
            <MailIcon className='size-4' />
            Email
          </ToggleButton>
        </div>
      </div>
      {value?.type === 'TRANSFER' && (
        <div className='space-y-1.5'>
          <Label>Phone number</Label>
          <PhoneInput
            value={value?.value || ''}
            onChange={(newValue) => onChange({ type: 'TRANSFER', value: newValue })}
            placeholder='+1 (555) 123-4567'
          />
        </div>
      )}
      {value?.type === 'EMAIL' && (
        <div className='space-y-1.5'>
          <Label>Email address</Label>
          <Input
            value={value?.value}
            onChange={(e) => onChange({ type: 'EMAIL', value: e.target.value })}
            placeholder='example@example.com'
          />
        </div>
      )}
    </div>
  );
}
